import { useEffect } from "react";
import {
  Compass, Users, GraduationCap, Laptop, HeartHandshake,
  BadgeCheck, Handshake, ShieldCheck, FileText,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const ABSCHNITTE = [
  {
    nr: "1", id: "auftrag", icon: Compass, titel: "Unser Auftrag",
    intro: "Wir qualifizieren arbeitsuchende Menschen und Mitarbeitende in der Pflege und Betreuung so, dass sie",
    punkte: [
      "fachlich gut vorbereitet,",
      "persönlich gestärkt und",
      "sicher im Umgang mit pflegerischen und betreuerischen Aufgaben",
    ],
    outro: "in den Arbeitsmarkt starten oder sich dort weiterentwickeln können.",
  },
  {
    nr: "2", id: "teilnehmende", icon: Users, titel: "Unsere Teilnehmenden",
    intro: "Im Mittelpunkt unseres Handelns stehen unsere Teilnehmenden. Wir begegnen ihnen mit Wertschätzung, Respekt und auf Augenhöhe – unabhängig von Herkunft, Geschlecht, Alter, Religion oder Lebenssituation. Wir",
    punkte: [
      "beraten individuell und ehrlich,",
      "berücksichtigen Vorerfahrungen und Lebenslagen,",
      "unterstützen beim Lernen und bei persönlichen Herausforderungen im Rahmen unserer Möglichkeiten.",
    ],
  },
  {
    nr: "3", id: "bildungsverstaendnis", icon: GraduationCap, titel: "Unser Bildungsverständnis",
    intro: "Lernen verstehen wir als gemeinsamen Prozess. Wir arbeiten praxisnah, erwachsenengerecht und handlungsorientiert. Dazu gehören für uns:",
    punkte: [
      "klar strukturierte Inhalte mit hohem Praxisbezug,",
      "anschauliche Beispiele aus dem Pflege- und Betreuungsalltag,",
      "ein Lernklima, in dem Fragen erlaubt und Fehler Lernchancen sind.",
    ],
  },
  {
    nr: "4", id: "digital-praesenz", icon: Laptop, titel: "Digitale und Präsenzangebote",
    intro: "Wir nutzen moderne Lernformen, um Bildung flexibel und erreichbar zu machen. Unsere Angebote finden in Präsenz, als Online-Live-Unterricht oder in hybriden Formaten statt.",
    punkte: [
      "Wir setzen auf verständliche Technik und klare Abläufe.",
      "Wir unterstützen unsere Teilnehmenden aktiv bei der Nutzung digitaler Lernwege.",
      "Datensicherheit und der sorgfältige Umgang mit personenbezogenen Daten sind für uns selbstverständlich.",
    ],
  },
  {
    nr: "5", id: "team", icon: HeartHandshake, titel: "Unsere Trainer:innen und Mitarbeitenden",
    intro: "Unsere Trainer:innen und Mitarbeitenden sind fachlich qualifiziert, pädagogisch geeignet und bringen aktuelle Praxiserfahrung mit – insbesondere aus Pflege, Betreuung und Bildung. Wir legen Wert darauf, dass sie",
    punkte: [
      "sich regelmäßig fortbilden,",
      "ihre Erfahrungen im Team teilen,",
      "mit Engagement, Klarheit und Herz unterrichten und begleiten.",
    ],
  },
  {
    nr: "6", id: "qualitaet", icon: BadgeCheck, titel: "Qualität und Weiterentwicklung",
    intro: "Qualität ist für uns ein kontinuierlicher Prozess. Dazu gehören insbesondere:",
    punkte: [
      "ein gelebtes Qualitätsmanagementsystem nach AZAV,",
      "regelmäßige Teilnehmerbefragungen und Verbleibserhebungen,",
      "der offene Umgang mit Rückmeldungen, Beschwerden und Verbesserungsvorschlägen,",
      "interne Audits und Managementbewertungen, aus denen wir konkrete Maßnahmen ableiten.",
    ],
    outro: "Wir prüfen regelmäßig, ob unsere Ziele erreicht werden, und passen unsere Angebote an die Anforderungen von Teilnehmenden, Betrieben und Kostenträgern an.",
  },
  {
    nr: "7", id: "partner", icon: Handshake, titel: "Zusammenarbeit mit Partnern",
    intro: "Wir arbeiten vertrauensvoll mit Betrieben, Einrichtungen, Kostenträgern und weiteren Partnern zusammen. Uns ist wichtig, dass alle Beteiligten transparent informiert werden und wissen, was sie von uns erwarten können.",
    punkte: [],
    outro: "Ziel dieser Zusammenarbeit ist es, für unsere Teilnehmenden reale berufliche Perspektiven zu schaffen und die Qualität in Pflege und Betreuung zu stärken.",
  },
  {
    nr: "8", id: "verantwortung", icon: ShieldCheck, titel: "Verantwortung",
    intro: "Wir handeln verantwortungsbewusst – gegenüber unseren Teilnehmenden, Partnern und Mitarbeitenden. Dazu zählen:",
    punkte: [
      "die Einhaltung gesetzlicher Vorgaben,",
      "der Schutz von Gesundheit und Sicherheit,",
      "der respektvolle Umgang miteinander,",
      "der bewusste Einsatz unserer Ressourcen.",
    ],
  },
];

export default function Leitbild() {
  useEffect(() => {
    document.title = "Unser Leitbild · Dickies-Campus GmbH | AZAV-zertifizierter Bildungsträger";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = "Das Leitbild der Dickies-Campus GmbH: Auftrag, Bildungsverständnis, Qualität und Verantwortung des AZAV-zugelassenen Bildungsträgers (Reg.-Nr. 0184.1-10141-AZAV-T).";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24" data-testid="leitbild-page">
        <span className="eyebrow">Über uns · Qualität</span>
        <h1 className="mt-3 font-heading text-4xl md:text-5xl font-semibold text-sand-800 tracking-tight">
          Unser Leitbild
        </h1>
        <p className="mt-5 text-sand-700 text-lg leading-relaxed" data-testid="leitbild-intro">
          Wir begleiten Menschen auf ihrem Weg in Qualifizierung und Beruf. Als AZAV-zertifizierter
          Bildungsträger sind wir darauf spezialisiert, arbeitsuchende Menschen und Beschäftigte im
          Pflege- und Betreuungsbereich gezielt zu unterstützen und nachhaltig zu stärken.
        </p>

        <div className="mt-12 space-y-10">
          {ABSCHNITTE.map(({ nr, id, icon: Icon, titel, intro, punkte, outro }) => (
            <section key={id} id={id} className="rounded-2xl border border-sand-800/10 bg-white/70 p-6 sm:p-8" data-testid={`leitbild-abschnitt-${nr}`}>
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 rounded-full bg-blush/25 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-terra-dark" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-widest text-terra font-bold">Abschnitt {nr}</div>
                  <h2 className="mt-1 font-heading text-2xl text-sand-800 font-semibold">{titel}</h2>
                </div>
              </div>
              <p className="mt-5 text-sand-700 text-base leading-relaxed">{intro}</p>
              {punkte.length > 0 && (
                <ul className="mt-4 space-y-2.5">
                  {punkte.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sand-700 text-base leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terra shrink-0" aria-hidden="true" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              {outro && <p className="mt-4 text-sand-700 text-base leading-relaxed">{outro}</p>}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-2xl bg-sage/20 border border-sage/40 p-6 sm:p-8" data-testid="leitbild-anspruch">
          <p className="text-sand-800 text-base leading-relaxed font-semibold">
            Dieses Leitbild ist Orientierung und Anspruch zugleich.
          </p>
          <p className="mt-2 text-sand-700 text-base leading-relaxed">
            Wir lassen uns daran messen – im täglichen Handeln, im Unterricht, in der Beratung
            und in der Zusammenarbeit mit allen Beteiligten.
          </p>
        </div>

        <div className="mt-12 rounded-2xl border border-sand-800/10 bg-white/70 p-6 sm:p-8" data-testid="leitbild-azav-box">
          <div className="flex items-center gap-2 text-terra font-bold text-xs uppercase tracking-widest">
            <BadgeCheck className="w-4 h-4" /> AZAV-zugelassener Bildungsträger
          </div>
          <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <img
              src="/apv-zeichen-azav.png"
              alt="Zertifizierungszeichen der APV-Zertifizierungs GmbH"
              className="w-32 h-auto shrink-0 rounded-lg bg-white p-2 border border-sand-800/10"
              data-testid="leitbild-apv-zeichen-img"
            />
            <p className="text-sand-700 text-base leading-relaxed">
              Die Dickies-Campus GmbH ist durch die APV-Zertifizierungs GmbH als Träger nach
              § 178 SGB III und AZAV zugelassen (Zertifikats-Nr. 0184.1-10141-AZAV-T,
              Fachbereiche 01 und 04, gültig bis 13.02.2029). Dieses Leitbild ist Teil unseres
              gelebten Qualitätsmanagementsystems.
            </p>
          </div>
          <a
            href="/azav-zulassung"
            data-testid="leitbild-link-azav"
            className="mt-4 inline-block text-terra font-semibold underline underline-offset-4 hover:text-sand-800"
          >
            Alle Angaben zur AZAV-Zulassung ansehen →
          </a>
        </div>

        <div className="mt-10 flex items-start gap-3 text-sand-700/80 text-sm leading-relaxed" data-testid="leitbild-quelle">
          <FileText className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
          <p>
            Quelle: Gelenktes QM-Dokument NW_QM_01_Leitbild · Version V01 · Stand 11/2025 ·
            Freigabe: Bereich QM. Diese Web-Fassung entspricht inhaltlich der gelenkten
            Dokumentenversion; Aktualisierungen erfolgen bei jeder Leitbild-Revision.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
